import React from 'react';
import cn from 'classnames';

import { getTranslation } from 'helpers/getTranslation';

import { Typography } from 'components/shared/Typography';
import { Rating } from 'components/shared/Rating';
import { Loading } from 'components/shared/Loading';

import { EmptyState } from 'components/EmptyState';

import { LessonRatingsCourseSelect } from './LessonRatingsCourseSelect';
import { LessonRatingsRow } from './components/LessonRatingsRow';

import {
  formatRatingScore,
  getModuleTitle,
  getLessonTitle,
} from './LessonRatingsCard.helpers';

import styles from './LessonRatingsCard.module.scss';

const LessonRatingsCard = ({
  className,
  loadingCourses,
  loadingRatings,
  hasCourses,
  courseOptions,
  currentCourseOption,
  aggregatedData,
  expandedModuleIds,
  onCourseChange,
  onModuleToggle,
}: {
  className: any;
  loadingCourses: any;
  loadingRatings: any;
  hasCourses: any;
  courseOptions: any;
  currentCourseOption: any;
  aggregatedData: any;
  expandedModuleIds: any;
  onCourseChange: any;
  onModuleToggle: any;
}) => {
  if (loadingCourses) {
    return (
      <div className={cn(styles.card, className)}>
        <Loading className={styles.loading} />
      </div>
    );
  }

  if (!hasCourses) {
    return (
      <div className={cn(styles.card, className)}>
        <Typography
          className={styles.title}
          variant="body1"
          weight="medium"
          mode="compact"
        >
          {getTranslation('dashboard_page.lesson_ratings_card.title')}
        </Typography>
        <EmptyState
          className={styles.emptyState}
          icon="drafts"
          title={getTranslation(
            'dashboard_page.lesson_ratings_card.no_courses_empty_state.title',
          )}
          text={getTranslation(
            'dashboard_page.lesson_ratings_card.no_courses_empty_state.text',
          )}
        />
      </div>
    );
  }

  const { courseAverage, totalCount, modules } = aggregatedData;
  const hasRatings = totalCount > 0;

  return (
    <div className={cn(styles.card, className)}>
      <div className={styles.header}>
        <Typography
          className={styles.title}
          variant="body1"
          weight="medium"
          mode="compact"
        >
          {getTranslation('dashboard_page.lesson_ratings_card.title')}
        </Typography>
        <div className={styles.headerControls}>
          <LessonRatingsCourseSelect
            className={styles.courseSelect}
            options={courseOptions}
            value={currentCourseOption}
            onChange={onCourseChange}
          />
        </div>
      </div>

      {loadingRatings ? (
        <Loading className={styles.loading} />
      ) : !hasRatings ? (
        <EmptyState
          className={styles.emptyState}
          icon="mail"
          title={getTranslation(
            'dashboard_page.lesson_ratings_card.empty_state.title',
          )}
          text={getTranslation(
            'dashboard_page.lesson_ratings_card.empty_state.text',
          )}
        />
      ) : (
        <>
          <div className={styles.summary}>
            <Typography
              className={styles.summaryLabel}
              variant="caption"
              mode="compact"
            >
              {getTranslation(
                'dashboard_page.lesson_ratings_card.course_average',
              )}
            </Typography>
            <div className={styles.summaryContent}>
              <Typography
                className={styles.summaryScore}
                variant="subtitle"
                weight="medium"
                mode="compact"
              >
                {formatRatingScore(courseAverage)}
              </Typography>
              <Rating
                className={styles.summaryRating}
                readonly
                value={courseAverage}
                size={20}
              />
              <Typography
                className={styles.summaryCount}
                variant="body2"
                mode="compact"
              >
                {getTranslation(
                  'dashboard_page.lesson_ratings_card.ratings_count',
                  { count: totalCount },
                )}
              </Typography>
            </div>
          </div>

          <Typography
            className={styles.sectionTitle}
            variant="body2"
            weight="medium"
            mode="compact"
          >
            {getTranslation('dashboard_page.lesson_ratings_card.by_modules')}
          </Typography>

          <div className={styles.list}>
            <div className={styles.listHeader}>
              <Typography
                className={styles.listHeaderCell}
                variant="caption"
                weight="medium"
                mode="compact"
              >
                {getTranslation(
                  'dashboard_page.lesson_ratings_card.table.title',
                )}
              </Typography>
              <Typography
                className={styles.listHeaderCell}
                variant="caption"
                weight="medium"
                mode="compact"
              >
                {getTranslation(
                  'dashboard_page.lesson_ratings_card.table.score',
                )}
              </Typography>
              <Typography
                className={styles.listHeaderCell}
                variant="caption"
                weight="medium"
                mode="compact"
              >
                {getTranslation(
                  'dashboard_page.lesson_ratings_card.table.average',
                )}
              </Typography>
              <Typography
                className={styles.listHeaderCell}
                variant="caption"
                weight="medium"
                mode="compact"
              >
                {getTranslation(
                  'dashboard_page.lesson_ratings_card.table.count',
                )}
              </Typography>
            </div>

            {modules.map((module: any) => {
              const isExpanded = expandedModuleIds[module.id];

              return (
                <React.Fragment key={module.id}>
                  <LessonRatingsRow
                    title={getModuleTitle(module.title)}
                    averageRating={module.averageRating}
                    count={module.count}
                    isModule
                    isExpanded={isExpanded}
                    onToggle={() => onModuleToggle(module.id)}
                  />

                  {isExpanded &&
                    module.lessons.map((lesson: any) => (
                      <LessonRatingsRow
                        key={lesson.id}
                        title={getLessonTitle(lesson.title)}
                        averageRating={lesson.averageRating}
                        count={lesson.count}
                        isLesson
                      />
                    ))}
                </React.Fragment>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default React.memo(LessonRatingsCard) as any;
